// ===================================================================
// FEEDBACK SCORING ENGINE - FIXED VERSION
// Rule-based keyword and pattern detection for MVP
// ===================================================================

export function scoreResponse(userResponse, rubric) {
  const text = userResponse.toLowerCase();
  
  // Score each dimension
  const clarity = scoreClarity(text, userResponse);
  const respect = scoreRespect(text, userResponse);
  const solutionFocus = scoreSolutionFocus(text, userResponse);
  const assertiveness = scoreAssertiveness(text, userResponse);
  
  // Generate feedback for each dimension
  const clarityFeedback = getClarityFeedback(clarity, text);
  const respectFeedback = getRespectFeedback(respect, text);
  const solutionFocusFeedback = getSolutionFocusFeedback(solutionFocus, text);
  const assertivenessFeedback = getAssertivenessFeedback(assertiveness, text);
  
  // Generate strengths and improvements
  const strengths = [];
  const improvements = [];
  
  if (clarity >= 4) strengths.push("You asked specific, answerable questions");
  else if (clarity <= 2) improvements.push("Try asking more specific questions with multiple-choice options");
  
  if (respect >= 4) strengths.push("Professional tone without over-apologizing");
  else if (respect <= 2) improvements.push("Reduce apologies—asking for clarity isn't a mistake");
  
  if (solutionFocus >= 4) strengths.push("Solution-oriented approach that's easy for the manager to respond to");
  else if (solutionFocus <= 2) improvements.push("Frame questions to be easy to answer (yes/no or multiple choice)");
  
  if (assertiveness >= 4) strengths.push("Direct and confident without being aggressive");
  else if (assertiveness <= 2) improvements.push("Be more direct—reduce hedging words like 'just', 'maybe', 'wondering'");
  
  // Ensure at least one strength
  if (strengths.length === 0) {
    strengths.push("You took the initiative to respond—that's the first step");
  }
  
  return {
    clarity,
    respect,
    solutionFocus,
    assertiveness,
    clarityFeedback,
    respectFeedback,
    solutionFocusFeedback,
    assertivenessFeedback,
    strengths,
    improvements
  };
}

// ===================================================================
// CLARITY SCORING
// ===================================================================

function scoreClarity(text, originalText) {
  const questionMarks = (originalText.match(/\?/g) || []).length;
  const specificNouns = countSpecificNouns(text);
  const hasMultipleChoice = hasMultipleChoicePattern(text);
  
  // 5/5: 3+ questions, specific nouns, multiple choice options
  if (questionMarks >= 3 && specificNouns >= 3 && hasMultipleChoice) {
    return 5;
  }
  
  // 4/5: 2-3 questions with specific details
  if (questionMarks >= 2 && specificNouns >= 2) {
    return 4;
  }
  
  // 3/5: At least 1 question with some specificity
  if (questionMarks >= 1 && specificNouns >= 1) {
    return 3;
  }
  
  // 2/5: Vague questions only
  if (questionMarks >= 1) {
    return 2;
  }
  
  // 1/5: No questions asked
  return 1;
}

function countSpecificNouns(text) {
  const specificKeywords = [
    'deck', 'client', 'project', 'falcon', 'proposal', 'update', 'performance',
    'data', 'recommendations', 'formatting', 'pm', 'eod', 'timing', 'deadline',
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'morning', 'afternoon'
  ];
  
  return specificKeywords.filter(keyword => text.includes(keyword)).length;
}

function hasMultipleChoicePattern(text) {
  // Look for patterns like "A / B / C" or "A or B" or numbered lists
  const patterns = [
    /\w+\s*\/\s*\w+/,  // "option A / option B"
    /\w+\s+or\s+\w+/,  // "option A or option B"
    /\d+\./,            // "1. option A"
    /\([a-z]\)/,        // "(a) option A"
  ];
  
  return patterns.some(pattern => pattern.test(text));
}

function getClarityFeedback(score, text) {
  if (score === 5) {
    return "Excellent! Your questions are specific and give the manager easy options to choose from.";
  } else if (score === 4) {
    return "Good specificity. Consider adding multiple-choice options to make it even easier to respond.";
  } else if (score === 3) {
    return "You asked questions, but they could be more specific. Try: 'Which deck: A, B, or C?'";
  } else if (score === 2) {
    return "Your questions are too vague. Be more specific about what you need to know.";
  } else {
    return "You didn't ask clarifying questions. Remember: asking = professional, guessing = risky.";
  }
}

// ===================================================================
// RESPECT SCORING - FIXED VERSION
// ===================================================================

function scoreRespect(text, originalText) {
  const apologyCount = countApologies(text);
  const softeners = countSofteners(text);
  const isProfessional = hasProfessionalTone(text, originalText);
  
  // 5/5: 0 apologies, 0-2 softeners, professional
  if (apologyCount === 0 && softeners <= 2 && isProfessional) {
    return 5;
  }
  
  // 4/5: 1-2 apologies, professional
  if (apologyCount <= 2 && isProfessional) {
    return 4;
  }
  
  // 3/5: 3+ apologies but still respectful
  if (apologyCount >= 3 && isProfessional) {
    return 3;
  }
  
  // 2/5: Too deferential or too casual
  if (apologyCount > 5 || !isProfessional) {
    return 2;
  }
  
  // 1/5: Disrespectful
  return 1;
}

function countApologies(text) {
  const apologyWords = ['sorry', 'apologize', 'apologies', 'forgive'];
  return apologyWords.reduce((count, word) => {
    const matches = text.match(new RegExp(word, 'gi'));
    return count + (matches ? matches.length : 0);
  }, 0);
}

function countSofteners(text) {
  const softenerPhrases = [
    'quick', 'just', 'brief', 'small', 'tiny', 'little',
    'if you don\'t mind', 'when you get a chance', 'no rush'
  ];
  
  return softenerPhrases.filter(phrase => text.includes(phrase)).length;
}

function hasProfessionalTone(text, originalText) {
  // Trim and normalize
  const normalized = originalText.trim().toLowerCase();
  
  // Check for greeting (more flexible - check first 50 characters)
  const firstPart = normalized.substring(0, 50);
  const hasGreeting = /\b(hi|hello|hey|good morning|good afternoon)\b/i.test(firstPart);
  
  // Check for sign-off
  const hasSignOff = /(thanks|thank you|regards|best|cheers)/i.test(text);
  
  // Check for unprofessional markers
  const hasSlang = /(gonna|wanna|gotta|dunno|yeah|nah|lol|omg)/i.test(text);
  const hasAllCaps = /[A-Z]{4,}/.test(originalText);
  
  return hasGreeting && hasSignOff && !hasSlang && !hasAllCaps;
}

function getRespectFeedback(score, text) {
  if (score === 5) {
    return "Perfect professional tone—warm but not over-apologetic.";
  } else if (score === 4) {
    return "Professional and polite. Minor apologies are fine, but watch for overdoing it.";
  } else if (score === 3) {
    return "Too many apologies. Asking for clarity isn't a mistake—it's good practice.";
  } else if (score === 2) {
    return "Either too apologetic or too casual. Aim for professional warmth.";
  } else {
    return "Tone needs work. Be respectful and professional in all work communications.";
  }
}

// ===================================================================
// SOLUTION-FOCUS SCORING
// ===================================================================

function scoreSolutionFocus(text, originalText) {
  const hasQuestions = /\?/.test(originalText);
  const hasMultipleChoice = hasMultipleChoicePattern(text);
  const hasProposedSolution = proposedSolution(text);
  const focusesOnProblem = focusesOnProblemOnly(text);
  
  // 5/5: Multiple choice or proposed next steps
  if (hasMultipleChoice || hasProposedSolution) {
    return 5;
  }
  
  // 4/5: Clear yes/no questions
  if (hasQuestions && !focusesOnProblem) {
    return 4;
  }
  
  // 3/5: Open-ended questions only
  if (hasQuestions) {
    return 3;
  }
  
  // 2/5: Focuses on problem without seeking clarity
  if (focusesOnProblem) {
    return 2;
  }
  
  // 1/5: Just complains
  return 1;
}

function proposedSolution(text) {
  const solutionPhrases = [
    'i can', 'i will', 'i\'ll', 'let me', 'how about',
    'would you like', 'i propose', 'i suggest', 'happy to'
  ];
  
  return solutionPhrases.some(phrase => text.includes(phrase));
}

function focusesOnProblemOnly(text) {
  const problemPhrases = [
    'confused', 'unclear', 'don\'t understand', 'not sure what',
    'don\'t know which', 'can\'t tell'
  ];
  
  const hasProblemLanguage = problemPhrases.some(phrase => text.includes(phrase));
  const hasQuestions = /\?/.test(text);
  
  return hasProblemLanguage && !hasQuestions;
}

function getSolutionFocusFeedback(score, text) {
  if (score === 5) {
    return "Excellent! You made it easy for the manager to respond quickly with clear options.";
  } else if (score === 4) {
    return "Good questions. Consider offering multiple-choice options for even faster responses.";
  } else if (score === 3) {
    return "Open-ended questions make more work for the manager. Try: 'Is it A or B?'";
  } else if (score === 2) {
    return "You described the problem but didn't ask specific questions to solve it.";
  } else {
    return "Focus on solutions, not problems. Ask clear questions to move forward.";
  }
}

// ===================================================================
// ASSERTIVENESS SCORING
// ===================================================================

function scoreAssertiveness(text, originalText) {
  const hedgingCount = countHedging(text);
  const isConfident = hasConfidentLanguage(text);
  const isAggressive = hasAggressiveTone(text);
  const excessiveUncertainty = hasExcessiveUncertainty(text);
  
  // 1/5: Aggressive
  if (isAggressive) {
    return 1;
  }
  
  // 1/5: Too passive (excessive uncertainty)
  if (excessiveUncertainty) {
    return 1;
  }
  
  // 5/5: Direct, confident, no hedging
  if (hedgingCount === 0 && isConfident) {
    return 5;
  }
  
  // 4/5: Mostly direct with minor hedging
  if (hedgingCount <= 2 && isConfident) {
    return 4;
  }
  
  // 3/5: Some confidence but noticeable hedging
  if (hedgingCount <= 4) {
    return 3;
  }
  
  // 2/5: Very tentative
  return 2;
}

function countHedging(text) {
  const hedgingPhrases = [
    'just', 'maybe', 'perhaps', 'possibly', 'might',
    'i think', 'i guess', 'i was wondering', 'if you could',
    'would you mind', 'i was hoping'
  ];
  
  return hedgingPhrases.filter(phrase => text.includes(phrase)).length;
}

function hasConfidentLanguage(text) {
  const confidentPhrases = [
    'i need', 'to confirm', 'to make sure', 'quick clarification',
    'to deliver', 'to prioritize', 'to get it right'
  ];
  
  return confidentPhrases.some(phrase => text.includes(phrase));
}

function hasAggressiveTone(text) {
  const aggressiveMarkers = [
    'obviously', 'clearly', 'you should', 'you need to',
    'this is unclear', 'that doesn\'t make sense'
  ];
  
  return aggressiveMarkers.some(marker => text.includes(marker));
}

function hasExcessiveUncertainty(text) {
  const uncertaintyPhrases = [
    'i don\'t know', 'i\'m not sure', 'i\'m confused',
    'i don\'t understand'
  ];
  
  const uncertaintyCount = uncertaintyPhrases.filter(phrase => 
    text.includes(phrase)
  ).length;
  
  return uncertaintyCount >= 2;
}

function getAssertivenessFeedback(score, text) {
  if (score === 5) {
    return "Perfect assertiveness—direct and confident without being aggressive.";
  } else if (score === 4) {
    return "Good confidence. Watch for hedging words like 'just' or 'maybe'.";
  } else if (score === 3) {
    return "Noticeable hedging. Be more direct: 'I need clarity on X' vs 'I was wondering if maybe...'.";
  } else if (score === 2) {
    return "Too tentative. You have the right to ask questions—state your needs directly.";
  } else {
    return "Either too passive (uncertain) or too aggressive (demanding). Aim for professional confidence.";
  }
}
